import * as angular from "angular";
import { CampaignDataProvider } from "../../data-provider/campaign.dp";
import { ExportDataProvider } from "../../data-provider/export.dp";
import { Group } from "../../models/group.model";
import { PermissionCheck } from "../../shared/permission";
import { DialogUtils } from "../../helper/dialog.helper";
import { ToastUtils } from "../../helper/toast.helper";
import { GroupDataProvider } from "../../data-provider/group.dp";
import { FilterDataProvider } from "../../data-provider/filter.dp";
import { FilterService } from "../../services/filter.service";

let GroupInfoComponent = {
    selector: "vcGroupInfo",
    templateUrl: "/angular/components/group/group-info.component.html",
    bindings: {
        group: "=",
        close: "&?",
        deleted: "&?",
        updated: "&?",
        size: "=?",
        refreshList: "&?",
        enableEditTurfMode: "&?",
        inDialog: "=?",
    },
    controller: class GroupInfoController {
        // * bindings for typescript compiler *
        public group: any;
        public close: any;
        public deleted: any;
        public updated: any;
        public refreshList: any;
        public enableEditTurfMode: any;
        public inDialog: boolean;
        public size: string;
        // ************************************

        public isLeaderView: boolean;
        public groupLoading: boolean = true;
        public autoFillList: boolean = false;

        private readonly $scope: any;
        private readonly $window: any;
        private readonly $location: any;
        private toast: ToastUtils;
        private dialog: DialogUtils;
        public __env: any;

        public dataProvider: GroupDataProvider;
        public exportDataProvider: ExportDataProvider;
        public permissionProvider: PermissionCheck;
        public campaignDataProvider: CampaignDataProvider;
        private filterDataProvider: FilterDataProvider;
        private filterService: FilterService;

        constructor(
            $scope: any,
            __env: any,
            $location: any,
            $window: any,
            GroupDataProvider: GroupDataProvider,
            ToastUtils: ToastUtils,
            DialogUtils: DialogUtils,
            PermissionCheck: PermissionCheck,
            CampaignDataProvider: CampaignDataProvider,
            ExportDataProvider: ExportDataProvider,
            FilterDataProvider: FilterDataProvider,
            FilterService: FilterService
        ) {
            this.$scope = $scope;
            this.$location = $location;
            this.$window = $window;
            this.toast = ToastUtils;
            this.dialog = DialogUtils;
            this.__env = __env;
            this.filterDataProvider = FilterDataProvider;
            this.filterService = FilterService;

            this.dataProvider = GroupDataProvider;
            this.permissionProvider = PermissionCheck;
            this.campaignDataProvider = CampaignDataProvider;
            this.exportDataProvider = ExportDataProvider;
        }

        $onInit() {
            if (this.group && this.group.gid && this.group.filters) {
                this.autoFillList = true;
            }

            //passed in groups don't have counts or teams. Load group info
            this.$scope.$watch(
                () => this.group,
                () => this.loadGroup()
            );
        }

        setAutoFillList(newValue: boolean): void {
            this.autoFillList = newValue;
        }

        viewOnMap(group: Group): void {
            this.$window.location = `/#/map?gid=${group.gid}`;
        }

        toggleEditTurfMode(enable: boolean): void {
            this.enableEditTurfMode({ group: this.group, enable: enable });
        }

        refreshUpdateList(group: Group, updateFilters: boolean): void {
            if (!updateFilters) {
                group.filters = this.generateFilterObject();
            }
            this.refreshList({
                group: group,
                dynamic: this.autoFillList,
                updateFilters: updateFilters,
            });
        }

        loadGroup(): void {
            this.groupLoading = true;
            this.dataProvider.getGroup(this.group.gid).then((group: Group) => {
                this.groupLoading = false;
                angular.extend(this.group, group);
            });
        }

        openGroupStatsDialog(group: Group, ev: object): void {
            this.dialog
                .show({
                    component: "vc-group-info-dialog",
                    targetEvent: ev,
                    scope: this.$scope,
                    size: "large",
                    locals: {
                        group: group,
                    },
                })
                .then((newGroup: Group) => {
                    angular.extend(group, newGroup);
                });
        }

        editGroupModal(group: Group, assign: boolean, ev: object): void {
            this.dialog
                .show({
                    component: "vc-create-group",
                    targetEvent: ev,
                    scope: this.$scope,
                    locals: {
                        group: group,
                        assign: assign,
                        autoSplitAssign: false,
                    },
                })
                .then((newGroup: Group) => {
                    angular.extend(group, newGroup);
                    this.updated();
                });
        }

        deleteGroup(group: Group, ev: object): void {
            this.dialog
                .delete({
                    targetEvent: ev,
                    skipHide: true,
                    fullscreen: true,
                    locals: {
                        title: `Delete List?`,
                        bodyText: "They will be permanently deleted",
                    },
                })
                .then(() => {
                    this.dataProvider.removeGroup(group).then(
                        () => {
                            this.toast.success("List deleted");
                            this.deleted();
                        },
                        () => this.toast.error()
                    );
                });
        }

        toggleGroupStatus(group: Group, status: string): void {
            group.gstatus = status;
            group.update().then(
                () => this.toast.success("List status updated"),
                () => this.toast.error()
            );
        }

        toggleGroupDynamic(group: Group): void {
            this.filterDataProvider
                .deleteGroupFilter(parseInt(group.gid))
                .then(() => {
                    if (this.autoFillList) {
                        group.filters = {
                            fname: group.gname,
                            filters: this.generateFilterObject(),
                            gid: group.gid,
                        };
                        this.createFilterWithGid(group.filters);
                        group.update().then(
                            () => {
                                this.dataProvider
                                    .refreshDynamicList(group)
                                    .then(() => {
                                        this.loadGroup();
                                        this.toast.success("List updated");
                                    });
                            },
                            () => this.toast.error()
                        );
                    } else {
                        group.filters = null;
                        this.toast.success("List updated");
                    }
                });
        }

        generateFilterObject(): object {
            let filter = this.filterDataProvider.nonEmptyAppliedFilters(true);
            if (this.group.points) {
                filter["turf_cut"] = true;
            }
            return filter;
        }

        createFilterWithGid(filter: object): void {
            filter["filters"].street = null;
            this.filterService.create(filter).then((response: object) => {
                const json = response["data"];
                if (json.success) {
                    json.data.fid = json.id;
                }
            });
        }

        exportWalkingList(group: Group, offset: number): void {
            if (this.permissionProvider.hasPermission("EXPORT")) {
                this.exportDataProvider.walkingList(group.gid, offset).then(
                    () => {
                        this.toast
                            .action("Export Started", "Show")
                            .then((value: string) => {
                                if (value == "ok") {
                                    if (this.isLeaderView) {
                                        this.$location.url("/leader_exports");
                                    } else {
                                        this.$location.url("/exports");
                                    }
                                }
                            });
                    },
                    () => this.toast.error()
                );
            } else {
                this.dialog.show({
                    component: "vc-export-disabled-dialog",
                    size: "large",
                    scope: this.$scope,
                });
            }
        }

        exportGeoJson(group: Group): void {
            if (this.permissionProvider.hasPermission("EXPORT")) {
                this.exportDataProvider.geoJson(group.gid).then(
                    () => {
                        this.toast
                            .action("Export Started", "Show")
                            .then((value: string) => {
                                if (value == "ok") {
                                    if (this.isLeaderView) {
                                        this.$location.url("/leader_exports");
                                    } else {
                                        this.$location.url("/exports");
                                    }
                                }
                            });
                    },
                    () => this.toast.error()
                );
            } else {
                this.dialog.show({
                    component: "vc-export-disabled-dialog",
                    size: "large",
                    scope: this.$scope,
                });
            }
        }

        exportKml(group: Group): void {
            if (this.permissionProvider.hasPermission("EXPORT")) {
                this.exportDataProvider.kml(group.gid).then(
                    () => {
                        this.toast
                            .action("Export Started", "Show")
                            .then((value: string) => {
                                if (value == "ok") {
                                    if (this.isLeaderView) {
                                        this.$location.url("/leader_exports");
                                    } else {
                                        this.$location.url("/exports");
                                    }
                                }
                            });
                    },
                    () => this.toast.error()
                );
            } else {
                this.dialog.show({
                    component: "vc-export-disabled-dialog",
                    size: "large",
                    scope: this.$scope,
                });
            }
        }

        exportShapefile(group: Group): void {
            if (this.permissionProvider.hasPermission("EXPORT")) {
                this.exportDataProvider.shapefile(group.gid).then(
                    () => {
                        this.toast
                            .action("Export Started", "Show")
                            .then((value: string) => {
                                if (value == "ok") {
                                    if (this.isLeaderView) {
                                        this.$location.url("/leader_exports");
                                    } else {
                                        this.$location.url("/exports");
                                    }
                                }
                            });
                    },
                    () => this.toast.error()
                );
            } else {
                this.dialog.show({
                    component: "vc-export-disabled-dialog",
                    size: "large",
                    scope: this.$scope,
                });
            }
        }
    },
};

angular
    .module("group")
    .component(GroupInfoComponent.selector, GroupInfoComponent);
